/**
 * Activity Logger Hook
 * 
 * Custom hook for activity logging to backend for audit trail:
 * - User action logging
 * - View, create, update, delete tracking
 * - Module-based categorization
 * - Silent error handling
 * 
 * @module hooks/useActivityLogger
 * @param {string} [module='general'] - Module name for logging categorization
 * @returns {Object} Activity logging functions (logActivity, logView, logCreate, logUpdate, logDelete)
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const useActivityLogger = (module = 'general') => {
  const { _t } = useTranslation();
  
  const logActivity = useCallback(async (action, details = {}) => {
    try {
      const response = await fetch('/api/v1/activity-logs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('wms_access_token')}`
        },
        body: JSON.stringify({
          module,
          action,
          details,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          url: window.location.pathname
        })
      });

      if (!response.ok) { /* noop */ }
    } catch (error) {
      // Silently fail - don't interrupt user flow
    }
  }, [module]);

  const logView = useCallback((resource, resourceId) => {
    return logActivity('view', { resource, resource_id: resourceId });
  }, [logActivity]);

  const logCreate = useCallback((resource, data) => {
    return logActivity('create', { resource, data });
  }, [logActivity]);

  const logUpdate = useCallback((resource, resourceId, changes) => {
    return logActivity('update', { resource, resource_id: resourceId, changes });
  }, [logActivity]);

  const logDelete = useCallback((resource, resourceId) => {
    return logActivity('delete', { resource, resource_id: resourceId });
  }, [logActivity]);

  const logExport = useCallback((resource, format, filters) => {
    return logActivity('export', { resource, format, filters });
  }, [logActivity]);

  const logImport = useCallback((resource, fileInfo) => {
    return logActivity('import', { resource, file_info: fileInfo });
  }, [logActivity]);

  const logSearch = useCallback((query, filters) => {
    return logActivity('search', { query, filters });
  }, [logActivity]);

  return {
    logActivity,
    logView,
    logCreate,
    logUpdate,
    logDelete,
    logExport,
    logImport,
    logSearch
  };
};

export default useActivityLogger;

